import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import indicatorsMd from "../../content/tables/indicators.md";
import actionsMd from "../../content/tables/actions.md";
import { parseTableDoc, blocksToHtml, inlineToHtml, parseImpactLevels, parsePerLevelMultiplier } from "../../utils/referenceTables";
import "../../css/referenceTables.css";

// The two reference tables, edited as Markdown in src/content/tables/
const DOCS = {
  indicators: {
    url: indicatorsMd,
    groupLabel: "Category",
    itemLabel: "Indicator",
    itemPlural: "indicators",
    fields: ["Description", "Database", "How the indicator is calculated", "How the score is computed", "Limitations"],
  },
  actions: {
    url: actionsMd,
    groupLabel: "Indicator",
    itemLabel: "Action",
    itemPlural: "actions",
    fields: ["Action description", "Impact levels", "Multiplier", "References"],
  },
};

const cache = {};

const Html = ({ html, className = "rt-cell" }) => <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;

function LevelPill({ children }) {
  return <span className="rt-level" dangerouslySetInnerHTML={{ __html: inlineToHtml(children) }} />;
}

// Actions: impact levels and (shared or per-level) multiplier
function ActionCells({ item }) {
  const levels = parseImpactLevels(item.fields["Impact levels"]);
  const perLevel = parsePerLevelMultiplier(item.fields["Multiplier"] || "", levels);
  const levelsRaw = (item.fields["Impact levels"] || "").trim();
  return (
    <>
      <td data-label="Impact levels">
        {levels.length ? (
          <ul className="rt-levels">
            {levels.map((l) => (
              <li key={l.level}>
                <LevelPill>{l.level}</LevelPill>
                {l.description && <span dangerouslySetInnerHTML={{ __html: inlineToHtml(l.description) }} />}
              </li>
            ))}
          </ul>
        ) : (
          <span className="rt-muted">{levelsRaw ? "n/a" : "–"}</span>
        )}
      </td>
      <td data-label="Multiplier">
        {perLevel ? (
          <ul className="rt-levels">
            {levels.map((l) => (
              <li key={l.level}>
                <LevelPill>{l.level}</LevelPill>
                <span dangerouslySetInnerHTML={{ __html: inlineToHtml(perLevel[l.level]) }} />
              </li>
            ))}
          </ul>
        ) : (
          <Html html={blocksToHtml(item.fields["Multiplier"] || "")} />
        )}
      </td>
    </>
  );
}

export default function ReferenceTableDialog({ kind, open, onClose }) {
  const config = DOCS[kind];
  const [doc, setDoc] = useState(cache[kind] || null);
  const [error, setError] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open || doc) return;
    fetch(config.url)
      .then((r) => r.text())
      .then((text) => { cache[kind] = parseTableDoc(text); setDoc(cache[kind]); })
      .catch(() => setError(true));
  }, [open, doc, config.url, kind]);

  const jumpTo = (index) => {
    const box = contentRef.current;
    const row = box && box.querySelector(`[data-group="${index}"]`);
    if (!row) return;
    const head = box.querySelector("thead");
    const offset = head && getComputedStyle(head).display !== "none" ? head.offsetHeight : 0;
    box.scrollTo({ top: box.scrollTop + row.getBoundingClientRect().top - box.getBoundingClientRect().top - offset, behavior: "smooth" });
  };

  const count = doc ? doc.groups.reduce((n, g) => n + g.items.length, 0) : 0;
  const columns = [config.itemLabel, ...config.fields];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" scroll="paper"
      PaperProps={{ className: "rt-paper" }} aria-labelledby={`rt-title-${kind}`}>
      <div className="rt-head">
        <div className="rt-head-text">
          <h2 id={`rt-title-${kind}`} className="rt-title">{doc ? doc.meta.title : ""}</h2>
          {doc && doc.meta.summary && <p className="rt-summary">{doc.meta.summary}</p>}
          {doc && (
            <p className="rt-meta">
              <span>{count} {config.itemPlural}</span>
              {doc.meta.last_updated && <span>Last updated: {doc.meta.last_updated}</span>}
            </p>
          )}
        </div>
        <IconButton onClick={onClose} aria-label="Close" className="rt-close"><CloseIcon /></IconButton>
      </div>
      {doc && (
        <nav className="rt-jump" aria-label={`Jump to ${config.groupLabel.toLowerCase()}`}>
          {doc.groups.map((g, i) => (
            <button type="button" key={g.name} onClick={() => jumpTo(i)}>
              {g.name}<span>{g.items.length}</span>
            </button>
          ))}
        </nav>
      )}
      <DialogContent ref={contentRef} className="rt-content" dividers>
        {!doc && !error && <div className="rt-loading"><CircularProgress size={32} /></div>}
        {error && <p className="rt-muted">The table could not be loaded. Please try again.</p>}
        {doc && (
          <table className={`rt-table rt-${kind}`}>
            <thead>
              <tr>{columns.map((c) => <th key={c} scope="col">{c}</th>)}</tr>
            </thead>
            {doc.groups.map((g, gi) => (
              <tbody key={g.name}>
                <tr className="rt-group" data-group={gi}>
                  <th colSpan={columns.length} scope="colgroup">
                    <span className="rt-group-label">{config.groupLabel}</span>{g.name}
                  </th>
                </tr>
                {g.items.map((item) => (
                  <tr key={item.name} className="rt-row">
                    <th scope="row" className="rt-name">
                      {item.name}
                      {item.note && <Html html={blocksToHtml(item.note)} className="rt-cell rt-note" />}
                    </th>
                    <td data-label={config.fields[0]}><Html html={blocksToHtml(item.fields[config.fields[0]] || "")} /></td>
                    {kind === "actions" ? (
                      <ActionCells item={item} />
                    ) : (
                      config.fields.slice(1, -1).map((f) => (
                        <td key={f} data-label={f}><Html html={blocksToHtml(item.fields[f] || "")} /></td>
                      ))
                    )}
                    <td data-label={config.fields[config.fields.length - 1]} className="rt-last">
                      <Html html={blocksToHtml(item.fields[config.fields[config.fields.length - 1]] || "")} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
